import React, { useEffect, useState, useCallback } from 'react';
import streamService, { Stream, CreateStreamInput } from '../../services/streams';
import { logger } from '../../utils/logger';
import uploadService from '../../services/upload';
import { userService } from '../../services/userService';
import './Streams.css';

const AdminStreams: React.FC = () => {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [editingCoverId, setEditingCoverId] = useState<string | null>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');
  const [uploadingCover, setUploadingCover] = useState(false);

  const [formData, setFormData] = useState<CreateStreamInput>({
    name: '',
    description: '',
    genre: '',
    audioUrl: '',
    bitrate: 128,
    format: 'mp3'
  });

  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [createCoverFile, setCreateCoverFile] = useState<File | null>(null);
  const [createCoverPreview, setCreateCoverPreview] = useState<string>('');

  const loadStreams = useCallback(async () => {
    if (!currentUsername) return;

    try {
      setLoading(true);
      // Only load streams uploaded by the current user
      const data = await userService.getUserUploads(currentUsername);
      setStreams(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load streams');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const profile = await userService.getProfile();
        setCurrentUsername(profile.username);
      } catch (err: any) {
        setError('Failed to load user profile');
      }
    };

    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUsername) {
      loadStreams();
    }
  }, [currentUsername, loadStreams]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type - accept all common audio formats
      const validTypes = [
        'audio/mpeg',      // MP3
        'audio/mp3',       // MP3 (alternative MIME)
        'audio/mp4',       // M4A, M4B (MPEG-4 audio)
        'audio/x-m4a',     // M4A (alternative MIME)
        'audio/m4a',       // M4A (alternative MIME)
        'audio/aac',       // AAC
        'audio/ogg',       // OGG Vorbis
        'audio/opus',      // Opus
        'audio/wav',       // WAV
        'audio/x-wav',     // WAV (alternative MIME)
        'audio/wave',      // WAV (alternative MIME)
        'audio/flac',      // FLAC
        'audio/x-flac',    // FLAC (alternative MIME)
        'audio/alac',      // Apple Lossless (ALAC)
        'audio/x-alac',    // ALAC (alternative MIME)
        'audio/aiff',      // AIFF
        'audio/x-aiff',    // AIFF (alternative MIME)
        'audio/webm',      // WebM audio
        'audio/x-ms-wma'   // Windows Media Audio
      ];

      const isValidAudio = file.type.startsWith('audio/') ||
                          validTypes.includes(file.type) ||
                          file.name.match(/\.(mp3|m4a|m4b|aac|ogg|opus|wav|flac|alac|aiff|aif|aifc|webm|wma)$/i);

      if (!isValidAudio) {
        setError('Please select a valid audio file (MP3, M4A, AAC, OGG, Opus, WAV, FLAC, ALAC, AIFF, WebM, WMA)');
        return;
      }

      setAudioFile(file);
      setError(null);
    }
  };

  const handleCreateCoverImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setCreateCoverFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCreateCoverPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'bitrate' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    logger.info('Form submitted!', { formData, audioFile: audioFile?.name });

    if (!audioFile) {
      setError('Please select an audio file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(10);

      // Upload audio file
      logger.info('Starting upload for file', {
        name: audioFile.name,
        type: audioFile.type,
        size: audioFile.size
      });
      const uploadResult = await uploadService.uploadAudio(audioFile);
      logger.info('Upload successful!', { uploadResult });
      setUploadProgress(60);

      // Create stream with uploaded audio URL
      const streamInput: CreateStreamInput = {
        ...formData,
        audioUrl: uploadResult.url
      };

      setUploadProgress(80);
      const createdStream = await streamService.createStream(streamInput);
      setUploadProgress(90);

      // Upload cover image if selected
      if (createCoverFile && createdStream.streamId) {
        await streamService.uploadCoverImage(createdStream.streamId, createCoverFile);
      }
      setUploadProgress(100);

      // Reset form and reload streams
      setFormData({
        name: '',
        description: '',
        genre: '',
        audioUrl: '',
        bitrate: 128,
        format: 'mp3'
      });
      setAudioFile(null);
      setCreateCoverFile(null);
      setCreateCoverPreview('');
      setShowCreateForm(false);
      await loadStreams();

      alert('Stream created successfully!');
    } catch (err: any) {
      logger.error('Error creating stream', err, {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.message || 'Failed to create stream');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (streamId: string) => {
    if (!window.confirm('Are you sure you want to delete this stream?')) {
      return;
    }

    try {
      await streamService.deleteStream(streamId);
      await loadStreams();
      alert('Stream deleted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to delete stream');
    }
  };

  const handleStatusToggle = async (stream: Stream) => {
    try {
      const newStatus = stream.status === 'active' ? 'inactive' : 'active';
      await streamService.updateStream(stream.streamId, { status: newStatus });
      await loadStreams();
    } catch (err: any) {
      setError(err.message || 'Failed to update stream status');
    }
  };

  const handleCoverImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setCoverImageFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadCover = async (streamId: string) => {
    if (!coverImageFile) {
      setError('Please select an image file');
      return;
    }

    try {
      setUploadingCover(true);
      await streamService.uploadCoverImage(streamId, coverImageFile);
      await loadStreams();
      setEditingCoverId(null);
      setCoverImageFile(null);
      setCoverImagePreview('');
      alert('Cover image uploaded successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to upload cover image');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleDeleteCover = async (streamId: string) => {
    if (!window.confirm('Are you sure you want to delete the cover image?')) {
      return;
    }

    try {
      await streamService.deleteCoverImage(streamId);
      await loadStreams();
      alert('Cover image deleted successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to delete cover image');
    }
  };

  if (loading) {
    return (
      <div className="admin-streams">
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading your streams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-streams">
      {/* Hero Section */}
      <div className="admin-hero">
        <div className="admin-hero-content">
          <h1>Manage Streams</h1>
          <p className="admin-hero-subtitle">Upload, edit, and manage your audio streams</p>
        </div>
        <button
          className={`admin-create-btn ${showCreateForm ? 'cancel' : ''}`}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Cancel
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              New Stream
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError(null)} className="admin-error-close">&times;</button>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="admin-form-section">
          <h2 className="admin-form-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Create New Stream
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="name">Stream Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                className="admin-form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Jazz Sessions"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                className="admin-form-textarea"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={3}
                placeholder="Describe your stream..."
              />
            </div>

            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="genre">Genre *</label>
                <input
                  type="text"
                  id="genre"
                  name="genre"
                  className="admin-form-input"
                  value={formData.genre}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Jazz, Rock, Pop"
                />
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="bitrate">Audio Quality</label>
                <select
                  id="bitrate"
                  name="bitrate"
                  className="admin-form-select"
                  value={formData.bitrate}
                  onChange={handleInputChange}
                >
                  <option value="64">64 kbps</option>
                  <option value="96">96 kbps</option>
                  <option value="128">128 kbps</option>
                  <option value="192">192 kbps</option>
                  <option value="256">256 kbps</option>
                  <option value="320">320 kbps</option>
                  <option value="500">500 kbps</option>
                  <option value="1000">1000 kbps (Lossless)</option>
                  <option value="1411">1411 kbps (CD Quality)</option>
                  <option value="2400">2400 kbps (Hi-Res)</option>
                  <option value="4608">4608 kbps (Studio)</option>
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="format">Format</label>
                <select
                  id="format"
                  name="format"
                  className="admin-form-select"
                  value={formData.format}
                  onChange={handleInputChange}
                >
                  <option value="mp3">MP3</option>
                  <option value="m4a">M4A (Apple)</option>
                  <option value="aac">AAC</option>
                  <option value="ogg">OGG</option>
                  <option value="opus">Opus</option>
                  <option value="wav">WAV</option>
                  <option value="flac">FLAC</option>
                  <option value="alac">ALAC (Apple Lossless)</option>
                  <option value="aiff">AIFF</option>
                  <option value="webm">WebM</option>
                  <option value="wma">WMA</option>
                </select>
              </div>
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="createCoverImage">Cover Image (optional)</label>
              <input
                type="file"
                id="createCoverImage"
                className="admin-file-input"
                accept="image/*"
                onChange={handleCreateCoverImageSelect}
              />
              {createCoverPreview && (
                <div className="admin-cover-preview">
                  <img src={createCoverPreview} alt="Cover preview" />
                  <button
                    type="button"
                    onClick={() => {
                      setCreateCoverFile(null);
                      setCreateCoverPreview('');
                    }}
                    className="admin-cover-remove"
                  >
                    Remove
                  </button>
                </div>
              )}
              {createCoverFile && !createCoverPreview && (
                <p className="admin-file-info">
                  Selected: {createCoverFile.name} ({(createCoverFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="audioFile">Audio File *</label>
              <input
                type="file"
                id="audioFile"
                className="admin-file-input"
                accept="audio/*,.mp3,.m4a,.m4b,.aac,.ogg,.opus,.wav,.flac,.alac,.aiff,.aif,.aifc,.webm,.wma"
                onChange={handleFileChange}
                required
              />
              {audioFile && (
                <p className="admin-file-info">
                  Selected: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {uploading && (
              <div className="admin-upload-progress">
                <div className="admin-progress-bar">
                  <div
                    className="admin-progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="admin-progress-text">Uploading... {uploadProgress}%</p>
              </div>
            )}

            <div className="admin-form-actions">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowCreateForm(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={uploading}
              >
                {uploading ? 'Creating...' : 'Create Stream'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Streams List */}
      <div className="admin-streams-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">Your Streams</h2>
          <span className="admin-section-count">({streams.length})</span>
        </div>

        {streams.length === 0 ? (
          <div className="admin-empty-state">
            <svg className="admin-empty-icon" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
              <path d="M26 24L40 32L26 40V24Z" fill="currentColor" opacity="0.5"/>
            </svg>
            <h3 className="admin-empty-title">No streams yet</h3>
            <p className="admin-empty-text">Create your first stream above to get started!</p>
          </div>
        ) : (
          <div className="admin-streams-grid">
            {streams.map((stream) => (
              <div key={stream.streamId} className="admin-stream-card">
                {/* Cover Image */}
                <div className="admin-stream-cover">
                  {stream.coverImage ? (
                    <img src={stream.coverImage} alt={stream.name} />
                  ) : (
                    <div className="admin-stream-cover-placeholder">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M10 8l6 4-6 4V8z" fill="currentColor"/>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Stream Details */}
                <div className="admin-stream-details">
                  <h3 className="admin-stream-name">{stream.name}</h3>
                  <p className="admin-stream-description">{stream.description}</p>
                  <div className="admin-stream-meta">
                    <span className="admin-badge">{stream.genre}</span>
                    <span className="admin-badge">{stream.format?.toUpperCase() || 'MP3'}</span>
                    <span className="admin-badge">{stream.bitrate || 128} kbps</span>
                    <span className={`admin-badge admin-badge-${stream.status}`}>
                      {stream.status}
                    </span>
                  </div>

                  {/* Cover Image Editor */}
                  {editingCoverId === stream.streamId ? (
                    <div className="admin-cover-editor">
                      <label className="admin-cover-upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCoverImageSelect}
                        />
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {coverImagePreview ? 'Change image' : 'Choose cover image (max 5MB)'}
                      </label>
                      {coverImagePreview && (
                        <div className="admin-cover-preview-small">
                          <img src={coverImagePreview} alt="Preview" />
                        </div>
                      )}
                      <div className="admin-cover-actions">
                        <button
                          className="admin-cover-action-btn upload"
                          onClick={() => handleUploadCover(stream.streamId)}
                          disabled={!coverImageFile || uploadingCover}
                        >
                          {uploadingCover ? 'Uploading...' : 'Upload'}
                        </button>
                        <button
                          className="admin-cover-action-btn cancel"
                          onClick={() => {
                            setEditingCoverId(null);
                            setCoverImageFile(null);
                            setCoverImagePreview('');
                          }}
                          disabled={uploadingCover}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="admin-stream-actions">
                  {editingCoverId !== stream.streamId && (
                    <button
                      className="admin-action-btn admin-action-btn-cover"
                      onClick={() => setEditingCoverId(stream.streamId)}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                        <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {stream.coverImage ? 'Change' : 'Add Cover'}
                    </button>
                  )}
                  {stream.coverImage && editingCoverId !== stream.streamId && (
                    <button
                      className="admin-action-btn admin-action-btn-delete"
                      onClick={() => handleDeleteCover(stream.streamId)}
                    >
                      Remove Cover
                    </button>
                  )}
                  <button
                    className={`admin-action-btn ${stream.status === 'active' ? 'admin-action-btn-toggle' : 'admin-action-btn-activate'}`}
                    onClick={() => handleStatusToggle(stream)}
                  >
                    {stream.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="admin-action-btn admin-action-btn-delete"
                    onClick={() => handleDelete(stream.streamId)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStreams;
